/**
 * ARMAZENAMENTO QUE NÃO DERRUBA O APP EM ORIGEM OPACA.
 *
 * ── O DEFEITO QUE ISTO FECHA ──────────────────────────────────────────────
 *
 * O site publicado do tenant é servido com CSP `sandbox` SEM
 * `allow-same-origin` — de propósito: HTML autorado pelo tenant não pode
 * alcançar o storage do painel. O widget do chat abre um iframe do mesmo
 * domínio dentro dessa página, e o filho HERDA o sandbox: a página do chat roda
 * com origem OPACA.
 *
 * Nessa condição, o navegador não devolve `null` ao ler `localStorage` — ele
 * LANÇA `SecurityError`. Um único `getItem` fora de `try` no caminho de boot
 * (nosso ou de qualquer biblioteca) mata o `createApp` inteiro, e o resultado é
 * o painel do chat abrindo em branco: "o webchat não conecta". Foi exatamente
 * isso, reproduzido no navegador: `#app` renderizado como `<!---->`.
 *
 * ── POR QUE UM SHIM GLOBAL E NÃO UM `try` EM CADA CHAMADOR ────────────────
 *
 * Porque os nossos já estavam guardados — o que estourou veio de fora, e
 * caçar um `try` por dependência é uma corrida que se perde na próxima
 * atualização. Aqui a regra é dita uma vez: quando a origem é opaca, o
 * armazenamento é MEMÓRIA. A sessão do chat continua funcionando enquanto a
 * aba está aberta, que é a vida útil real de uma conversa de suporte, e nada
 * mais no app precisa saber que o chão mudou.
 *
 * Fora do sandbox nada é tocado: o `localStorage` de verdade continua sendo o
 * `localStorage` de verdade, e uma preferência salva ontem continua lá.
 */

/** O mínimo que um `Storage` precisa ter para o resto do app não notar. */
function memoryStorage(): Storage {
	const map = new Map<string, string>();
	return {
		get length() {
			return map.size;
		},
		key: (index: number) => [...map.keys()][index] ?? null,
		getItem: (key: string) => map.get(key) ?? null,
		setItem: (key: string, value: string) => void map.set(key, String(value)),
		removeItem: (key: string) => void map.delete(key),
		clear: () => map.clear(),
	} as Storage;
}

/**
 * Troca `window.localStorage`/`sessionStorage` por memória quando o de verdade
 * recusa.
 *
 * INSTALA-SE NO PRÓPRIO MÓDULO, e isso não é estilo: `import` é içado, então
 * uma chamada de função escrita no topo de `main.ts` correria DEPOIS de todos
 * os imports — inclusive dos que leem storage ao serem avaliados, que é o caso
 * a impedir. Módulos rodam na ordem em que são importados; ser o primeiro
 * `import` de `main.ts` é o que faz isto acontecer primeiro de verdade.
 */
function installStorageShim(): void {
	for (const name of ["localStorage", "sessionStorage"] as const) {
		try {
			// O teste tem de ESCREVER: em alguns navegadores a leitura passa e a
			// escrita é que estoura (modo privado do Safari, cota zerada).
			const probe = window[name];
			probe.setItem("__benvox_probe__", "1");
			probe.removeItem("__benvox_probe__");
		} catch {
			try {
				Object.defineProperty(window, name, {
					value: memoryStorage(),
					configurable: true,
				});
			} catch {
				// Um navegador que nem redefinir deixa: não há mais o que fazer aqui,
				// e quem lê storage guardado com `try` continua vendo o erro dele.
			}
		}
	}
}

installStorageShim();
